/* eslint-disable camelcase */
import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { loadStripe, PaymentMethod as StripePaymentMethod, Stripe } from '@stripe/stripe-js';
import PaymentMethodLogo from '@components/Icons/PaymentMethodLogo';
import ProgressIndicator from '@components/ProgressIndicator';
import PaymentMethodEditModal from '@modals/PaymentMethodEditModal';
import useNoodleApi from '@hooks/useNoodleApi';
import { getCreator, getUserSubscriptions } from '@tsClient';
import { Creator, SubscriptionStatus } from '@typings/graphql-models';
import { Elements } from '@stripe/react-stripe-js';
import Buttons from '@components/Buttons/Buttons';
import getMORStripeAccount from '@helpers/getMORStripeAccount';
import s from './CardDetails.module.scss';

type Props = {
  card: Pick<NonNullable<StripePaymentMethod['card']>, 'brand' | 'last4' | 'exp_month' | 'exp_year'> | null;
  isPrimary: boolean;
  isLoading?: boolean;
  creatorSlug: string;
  onSaveCard?: () => Promise<void>;
  subscription?: Awaited<ReturnType<typeof getUserSubscriptions>>[0];
  retryPayment?: (id: string) => Promise<void>;
};

const CardDetails: React.FC<Props> = ({ retryPayment, card, isPrimary, isLoading, creatorSlug, onSaveCard, subscription }) => {
  const { exp_month, exp_year } = card || {};
  const expiredDate = `${exp_month}/${exp_year?.toString().slice(-2)}`;
  let stateOfCard = null;

  const { getData: getCreatorFn } = useNoodleApi(getCreator);

  const [isRetrying, setIsRetrying] = useState(false);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [creator, setCreator] = useState<Pick<Creator, 'id' | 'stripe_account_id'> | null>(null);

  const isCardExpired = useMemo(() => {
    const expiration = new Date(exp_year as number, exp_month as number);

    const today = new Date();

    return today > expiration;
  }, [exp_month, exp_year]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { data } = await getCreatorFn({ creatorSlug });
      setCreator(data);
    };
    fetchData();
  }, [getCreatorFn, creatorSlug]);

  useEffect(() => {
    const loadStripeInstance = async (): Promise<void> => {
      if (!creator || !creator.stripe_account_id) return;
      const stripeAccount = getMORStripeAccount(creator);
      if (!stripeAccount) return;
      const newStripe = await loadStripe(process.env.NEXT_PUBLIC_STRIPE_KEY as string, { stripeAccount });
      setStripe(newStripe);
    };
    loadStripeInstance();
  }, [creator]);

  if (isCardExpired) {
    stateOfCard = 'Expired';
  } else if (isPrimary) {
    stateOfCard = 'Primary';
  }

  const handleClick = (): void => {
    setIsVisible(true);
  };

  const handleRetry = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (subscription && retryPayment) {
      setIsRetrying(true);
      await retryPayment(subscription.id);
      setIsRetrying(false);
    }
  };

  if (!stripe) {
    return null;
  }

  return (
    <Elements stripe={stripe}>
      <PaymentMethodEditModal stripe={stripe} creator={creator} isVisible={isVisible} setIsVisible={setIsVisible} onSaveCard={onSaveCard} />
      <button className={s.container} onClick={handleClick}>
        <PaymentMethodLogo brand={card?.brand || 'credit-card'} />
        <div className={s.content}>
          <p>{`•••• •••• •••• ${card?.last4}`}</p>
          <small className={classNames(s['card-details'], { [s['card-details--expired']]: isCardExpired })}>
            {expiredDate} {stateOfCard && ' · '}
            {stateOfCard && (
              <span className={classNames(s['card-details--isPrimary'], { [s['card-details--isExpired']]: isCardExpired })}>
                {isCardExpired ? 'Expired' : 'Primary'}
              </span>
            )}
          </small>
          {isLoading && <ProgressIndicator isAbsolute />}
        </div>
        {onSaveCard && <p className={s.editButton}>Edit</p>}
        {subscription?.subscriptionStatus === SubscriptionStatus.PastDue && retryPayment && (
          <Buttons isFetching={isRetrying} className={s.retry} onClick={handleRetry}>
            Retry
          </Buttons>
        )}
      </button>
    </Elements>
  );
};

export default CardDetails;
